import { Injectable } from '@angular/core';
import { from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { IReceivedPaymentInfo } from '../interface/iReceivedPaymentInfo';
import { ISendPaymentInfo } from '../interface/iSendPaymentInfo';

import * as uuid from 'uuid';
import { AmorphicService } from '../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { Product } from '../../../../../js/static/Product';
import { PaymentInfoResponse } from '../../../../../js/policyAdmin/PolicyAdminClient';
import { PaymentInfoPayload } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { UICategory, UILoggerSingleton } from '../../../../../../common/js/logger/UILoggerSingleton';

@Injectable()
export class PaymentService {
  controller;

  useSecondary: boolean;

  private static receivedPaymentInfo: IReceivedPaymentInfo;
  private static sendPaymentInfo: ISendPaymentInfo;
  private static policyNumber: string;

  private readonly channel: {};
  private channelName: string;
  private isTLIC: boolean;
  policyAdminDown: boolean = false;

  constructor(amorphicService: AmorphicService) {
    this.controller = amorphicService.controller;
    this.channel = Product.channels[amorphicService.controller.customer.application.primaryPolicy.getChannelCode()];
    this.channelName =
      Product.channels[amorphicService.controller.customer.application.primaryPolicy.getChannelCode()].name;
    this.isTLIC = amorphicService.controller.customer.application.primaryPolicy.isTLIC();
  }

  async getPaymentInfo(useSecondary?: boolean) {
    this.useSecondary = useSecondary;
    try {
      const response = await this.controller.getPaymentInfo(useSecondary);

      return this.createPaymentInfo(response);
    } catch (e) {
      this.policyAdminDown = true;
      UILoggerSingleton.getInstance().error(
        {
          module: 'payment-service',
          function: 'getPaymentInfo',
          category: UICategory.request,
          error: e,
        },
        'Failed to get payment info form Policy Admin'
      );
    }
  }

  createPaymentInfo(response: PaymentInfoResponse): IReceivedPaymentInfo {
    if (!response) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'payment-service',
          function: 'createPaymentInfo',
          category: UICategory.request,

          error: {
            isHumanRelated: false,
          },
        },
        'null response received from Policy Admin'
      );

      return {
        payerName: '',
        payerPartyId: '',
        bankAccountNumber: '',
        bankRoutingNumber: '',
        paymentAmount: '',
        effectiveDraftDate: '',
        frequency: '',
        isTLIC: false,
        policyAdminDown: true,
        channel: this.channel,
        company: '',
        productCode: '',
        paymentType: '',
      };
    }

    const body: IReceivedPaymentInfo = {
      payerName: response.policy.payer.payerName,
      payerPartyId: response.policy.payer.payerPartyId,
      bankAccountNumber: '',
      bankRoutingNumber: '',
      paymentAmount: response.policy.amount.toString(),
      effectiveDraftDate: response.policy.billing.nextPaymentDate,
      frequency: response.policy.billing.frequency,
      isTLIC: this.isTLIC,
      policyAdminDown: false,
      channel: this.channel,
      company: this.getCompany(response.policy.productCode),
      productCode: response.policy.productCode,
      paymentType: response.policy.payer.paymentType,
    };
    PaymentService.receivedPaymentInfo = body;
    return body;
  }

  getCompany(product: string): string {
    return 'Massachusetts Mutual';
  }

  getSendPaymentInfo(policyNumber?: string): ISendPaymentInfo {
    if (!PaymentService.sendPaymentInfo) {
      if (policyNumber) {
        PaymentService.sendPaymentInfo = {
          context: {
            requestId: <string>uuid.v1(),
          },
          payload: {
            policyNumber: <string | null>policyNumber,
            paymentMethod: {
              paymentAccount: null,
              paymentABA: null,
              paymentAddress: null,
              paymentName: null,
              accountType: null,
              businessType: null,
            },
          },
        };
      } else {
        throw new Error('To instantiate payment info object, please provide policy number.');
      }
    }
    return PaymentService.sendPaymentInfo;
  }

  updatePaymentInfoAndAuthorization(
    nextPaymentAuthorized: boolean,
    policyNumber: string,
    useSecondary: boolean,
    isAutoPay: boolean,
    isBillingPaused: boolean,
    paymentInfo?: PaymentInfoPayload
  ) {
    const body = {
      context: {
        requestId: uuid.v1(),
        userId: 'Owner',
      },
      payload: {
        policyNumber: policyNumber,
        updateInformation: {
          nextPaymentAuthorized,
          isAutoPay,
          isBillingPaused,
        },
        ...(paymentInfo && paymentInfo),
      },
    };

    const subscription = from(
      this.controller.updatePolicyInfoInPolicyAdmin('policy-admin/update-payer', body, useSecondary)
    );

    return subscription.pipe(
      map((response: Response) => response),
      catchError((error) => throwError(error || 'Server Error'))
    );
  }

  updatePaymentInfo(routingNumber: string, accountNumber: string, accountType: string) {
    const body = {
      context: {
        requestId: uuid.v1(),
        userId: 'Owner',
      },
      payload: {
        policyNumber: PaymentService.policyNumber,
        paymentInfo: {
          payerName: PaymentService.receivedPaymentInfo.payerName,
          payerPartyId: PaymentService.receivedPaymentInfo.payerPartyId,
          bankAccountNumber: accountNumber,
          bankRoutingNumber: routingNumber,
          paymentAmount: PaymentService.receivedPaymentInfo.paymentAmount,
          accountType: accountType.replace('_', ' '),
          effectiveDraftDate: PaymentService.receivedPaymentInfo.effectiveDraftDate,
          generateConfirmationPdf: true,
          frequency: PaymentService.receivedPaymentInfo.frequency,
        },
      },
    };
    const subscription = from(
      this.controller.updatePolicyInfoInPolicyAdmin('policy-admin/update-payer', body, this.useSecondary)
    );

    return subscription.pipe(
      map((response: Response) => response),
      catchError((error) => throwError(error || 'Server Error'))
    );
  }

  retryPaymentAuthorization(
    policyNumber: string,
    invoiceId: string,
    useSecondary: boolean,
    paymentInfo?: PaymentInfoPayload
  ) {
    const body = {
      context: {
        requestId: uuid.v1(),
        userId: 'Owner',
        eventName: '',
        referenceId: '',
        referenceType: '',
      },
      payload: {
        policyNumber: policyNumber,
        invoiceId: invoiceId,
        ...(paymentInfo && paymentInfo),
      },
    };

    const subscription = from(
      this.controller.updatePolicyInfoInPolicyAdmin('policy-admin/retry-payment', body, useSecondary)
    );

    return subscription.pipe(
      map((response: Response) => response),
      catchError((error) => throwError(error || 'Server Error'))
    );
  }

  reset() {
    PaymentService.sendPaymentInfo.payload.paymentMethod.paymentABA = null;
    PaymentService.sendPaymentInfo.payload.paymentMethod.paymentAccount = null;
    PaymentService.sendPaymentInfo.payload.paymentMethod.accountType = null;
    this.resetGiactResponse();
  }

  getChannelName(): string {
    return this.channelName;
  }

  resetGiactResponse() {
    const policy = this.controller.customer.application.primaryPolicy;
    const giactResponse = {
      normalizedResponseCode: null,
      status: null,
    };

    this.controller.giactController.updateLastWebDebitAttemptCombination(policy, null);
    this.controller.giactController.updatePaymentGiactInfo(policy, giactResponse);
  }
}
